import type { DocNode } from '@atlaskit/adf-schema';
import { scrubStr } from '@atlaskit/adf-utils/scrub';
import {
	ACTION,
	ACTION_SUBJECT,
	ACTION_SUBJECT_ID,
	EVENT_TYPE,
} from '@atlaskit/editor-common/analytics';
import { areNodesEqualIgnoreAttrs } from '@atlaskit/editor-common/utils/document';
import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import { fg } from '@atlaskit/platform-feature-flags';
import type { FireAnalyticEventCallback as ADFStreamerFireAnalyticEventCallback } from '@atlassian/ai-model-io/adf-streamer';
import { attributeError } from '@atlassian/editor-ai-common/analytics/attribute-error';
import type {
	Attribution,
	Confidence,
	DetectionLayer,
	OutcomeClass,
} from '@atlassian/editor-ai-common/analytics/normalized-telemetry-event';
import { ErrorUtils } from '@atlassian/editor-ai-common/utils/errors';
import type {
	EditorAIAnalyticEventPayload,
	ADFStreamingInfoAEP,
	ADFStreamingErrorAEP,
} from '@atlassian/editor-plugin-ai';

import { allStreamingCommands } from '../aiStreamingOrchestratorPluginType';
import type { API, StreamingCommand } from '../aiStreamingOrchestratorPluginType';
import type { QueuedStreamCommand } from '../types/StreamCommandQueue';

import type { ApplyErrorSymptom } from './utils/classify-apply-error';
import { getPmDoc } from './utils/get-pm-doc';
import { isAttributeOnlyChange } from './utils/is-attribute-only-change';

export function createFireADFStreamingAnalyticsEvent({
	api,
	isCreateAction,
	traceId,
	promptVersion,
}: {
	api: API;
	isCreateAction: boolean;
	promptVersion?: string;
	traceId: string | undefined;
}): ADFStreamerFireAnalyticEventCallback {
	return (event) => {
		if (fg('platform_editor_ai_traceid_in_analytics_events')) {
			const commonAttributes:
				| Omit<ADFStreamingErrorAEP['attributes'], 'errorMessage' | 'errorData'>
				| Omit<ADFStreamingInfoAEP['attributes'], 'message' | 'data'> = {
				traceId: fg('platform_editor_ai_traceid_in_analytics_events') ? traceId : undefined,
				...(promptVersion && fg('platform_editor_ai_promptversion_in_analytics')
					? { promptVersion }
					: {}),
				experienceName: null,
				selectionType: null,
				selectionCategory: null,
				streamId: null,
				source: { type: 'orchestrator', isCreateAction },
			};
			if (event.type === 'error') {
				api?.analytics?.actions.fireAnalyticsEvent<EditorAIAnalyticEventPayload, 'customEventType'>(
					{
						action: 'error',
						actionSubject: 'editorPluginAI',
						actionSubjectId: 'adfStreaming',
						eventType: EVENT_TYPE.OPERATIONAL,
						attributes: {
							...commonAttributes,
							errorMessage: event.message,
							errorData: event.data,
						},
					},
				);
			} else {
				api?.analytics?.actions.fireAnalyticsEvent<EditorAIAnalyticEventPayload, 'customEventType'>(
					{
						action: 'info',
						actionSubject: 'editorPluginAI',
						actionSubjectId: 'adfStreaming',
						eventType: EVENT_TYPE.OPERATIONAL,
						attributes: {
							...commonAttributes,
							message: event.message,
							data: event.data,
						},
					},
				);
			}
		} else {
			if (event.type === 'error') {
				api?.analytics?.actions.fireAnalyticsEvent<EditorAIAnalyticEventPayload, 'customEventType'>(
					{
						action: 'error',
						actionSubject: 'editorPluginAI',
						actionSubjectId: 'adfStreaming',
						eventType: EVENT_TYPE.OPERATIONAL,
						attributes: {
							errorMessage: event.message,
							errorData: event.data,
							experienceName: null,
							selectionType: null,
							selectionCategory: null,
							streamId: null,
							source: { type: 'orchestrator', isCreateAction },
						},
					},
				);
			} else {
				api?.analytics?.actions.fireAnalyticsEvent<EditorAIAnalyticEventPayload, 'customEventType'>(
					{
						action: 'info',
						actionSubject: 'editorPluginAI',
						actionSubjectId: 'adfStreaming',
						eventType: EVENT_TYPE.OPERATIONAL,
						attributes: {
							message: event.message,
							data: event.data,
							experienceName: null,
							selectionType: null,
							selectionCategory: null,
							streamId: null,
							source: { type: 'orchestrator', isCreateAction },
						},
					},
				);
			}
		}
	};
}

type AttributeOnlyReplaceNodePayload = {
	action: 'replaceNodeAction';
	actionSubject: 'editorPluginAI';
	actionSubjectId: 'orchestratorReplaceNode';
	attributes: {
		attributeOnlyChange: boolean;
		nodeName: string | undefined;
	};
	eventType: EVENT_TYPE.TRACK;
};

export function fireAttributeOnlyReplaceNodeAnalytics({
	api,
	initialNode,
	lastDoc,
}: {
	api: API;
	initialNode: PMNode | undefined;
	lastDoc: DocNode | undefined;
}): void {
	api?.analytics?.actions.fireAnalyticsEvent<AttributeOnlyReplaceNodePayload, 'customEventType'>({
		action: 'replaceNodeAction',
		actionSubject: 'editorPluginAI',
		actionSubjectId: 'orchestratorReplaceNode',
		eventType: EVENT_TYPE.TRACK,
		attributes: {
			attributeOnlyChange: isAttributeOnlyChange(api, initialNode, lastDoc),
			nodeName: initialNode?.type.name,
		},
	});
}

// Validates the command is valid ()
const validateCommandType = (cmd: StreamingCommand | undefined) =>
	cmd?.type && allStreamingCommands.includes(cmd.type) ? cmd.type : 'invalid';

export function checkAndFireNoDocumentChangeAnalytics({
	api,
	docBeforeStreaming,
	command,
	traceId,
}: {
	api: API;
	command: StreamingCommand | undefined;
	docBeforeStreaming: PMNode | undefined;
	traceId: string | undefined;
}): void {
	const docAfterStreaming = getPmDoc(api);
	if (docBeforeStreaming && docAfterStreaming) {
		const isSameDoc = docBeforeStreaming.eq(docAfterStreaming);
		const isSameDocIgnoreAttrs = areNodesEqualIgnoreAttrs(docBeforeStreaming, docAfterStreaming);
		if (isSameDoc || isSameDocIgnoreAttrs) {
			api.analytics?.actions.fireAnalyticsEvent({
				action: ACTION.NO_DOC_CHANGE_FOUND,
				actionSubject: ACTION_SUBJECT.AI_STREAMING,
				actionSubjectId: ACTION_SUBJECT_ID.EXPERIENCE_APPLICATION,
				eventType: EVENT_TYPE.OPERATIONAL,
				attributes: {
					command: fg('platform_editor_ai_traceid_in_analytics_events')
						? validateCommandType(command)
						: '',
					isSameDoc,
					isSameDocIgnoreAttrs,
					traceId: fg('platform_editor_ai_traceid_in_analytics_events') ? traceId : undefined,
				},
			});
		}
	}
}

export function fireADFStreamingEndSessionErrorAnalytics({
	api,
	sessionStarted,
	endSessionCallCount,
	lastEndedSessionId,
	isDoubleCall,
}: {
	api: API;
	endSessionCallCount: number;
	isDoubleCall: boolean;
	lastEndedSessionId: string | null;
	sessionStarted: boolean;
}): void {
	api?.analytics?.actions.fireAnalyticsEvent<EditorAIAnalyticEventPayload, 'customEventType'>({
		action: 'error',
		actionSubject: 'editorPluginAI',
		actionSubjectId: 'adfStreamingEndSession',
		eventType: EVENT_TYPE.OPERATIONAL,
		attributes: {
			sessionStarted,
			endSessionCallCount,
			lastEndedSessionId,
			isDoubleCall,
		},
	});
}

export function fireSessionStartedAnalytics({
	api,
	sessionId,
	traceId,
	promptVersion,
}: {
	api: API;
	promptVersion?: string;
	sessionId: string;
	traceId: string | undefined;
}): void {
	api?.analytics?.actions.fireAnalyticsEvent<EditorAIAnalyticEventPayload, 'customEventType'>({
		action: 'sessionStart',
		actionSubject: 'editorPluginAI',
		actionSubjectId: 'userFlow',
		eventType: EVENT_TYPE.UI,
		attributes: {
			aiGroupId: sessionId,
			actionTaken: undefined,
			editCount: 0,
			aiSessionId: '',
			traceIds: traceId ? [traceId] : undefined,
			...(promptVersion && fg('platform_editor_ai_promptversion_in_analytics')
				? { promptVersion }
				: {}),
		},
	});
}

export function fireToolCallCapturedAnalytics({
	api,
	command,
	actionId,
	traceId,
}: {
	actionId: string;
	api: API;
	command: StreamingCommand;
	traceId: string | undefined;
}): void {
	api?.analytics?.actions.fireAnalyticsEvent<EditorAIAnalyticEventPayload, 'customEventType'>({
		action: 'captured',
		actionSubject: 'editorPluginAI',
		actionSubjectId: 'toolCall',
		eventType: EVENT_TYPE.TRACK,
		attributes: {
			actionId,
			commandType: validateCommandType(command),
			contentType: 'contentType' in command ? command.contentType : undefined,
			traceId,
		},
	});
}

type SessionOutcomeAnalyticsPayload = {
	action: 'sessionOutcome';
	actionSubject: 'editorPluginAI';
	actionSubjectId: 'adfStreaming';
	attributes: {
		attribution: Attribution;
		confidence: Confidence;
		detectionLayer: DetectionLayer;
		finalAdfInvalid: boolean;
		finalAdfRepaired: boolean;
		finalJsonInvalid: boolean;
		finalJsonRepaired: boolean;
		outcomeClass: OutcomeClass;
		symptom?: string;
		traceId: string | undefined;
	};
	eventType: EVENT_TYPE.OPERATIONAL;
};

/**
 * Fire the authoritative "one event per session" outcome signal called for in
 * the Phase 1 rollout plan of the Editor + Minion ADF streaming telemetry
 * design.
 *
 * Carries the four normalized contract fields (`detection_layer`,
 * `attribution`, `outcome_class`, `confidence`) plus the raw repair signals
 * derived from the ADF streamer so dashboards can drill into the underlying
 * cause without re-deriving them.
 *
 * `attribution` and `confidence` are derived centrally via
 * {@link attributeError} from the supplied `(symptom, detectionLayer)` so
 * every event uses the same routing table. Callers therefore only need to
 * report a raw `symptom` string — they never hand-pick an attribution domain.
 */
export function fireSessionOutcomeAnalytics({
	api,
	traceId,
	outcomeClass,
	detectionLayer,
	symptom,
	finalJsonInvalid,
	finalJsonRepaired,
	finalAdfInvalid,
	finalAdfRepaired,
}: {
	api: API;
	detectionLayer: DetectionLayer;
	finalAdfInvalid: boolean;
	finalAdfRepaired: boolean;
	finalJsonInvalid: boolean;
	finalJsonRepaired: boolean;
	outcomeClass: OutcomeClass;
	symptom?: string;
	traceId: string | undefined;
}): void {
	// When no symptom is reported (e.g. clean_success) the routing table
	// returns `unknown`/`low`, which is the correct contract value for "no
	// detectable failure to attribute".
	const { attribution, confidence } = attributeError(symptom ?? '', detectionLayer);

	api?.analytics?.actions.fireAnalyticsEvent<SessionOutcomeAnalyticsPayload, 'customEventType'>({
		action: 'sessionOutcome',
		actionSubject: 'editorPluginAI',
		actionSubjectId: 'adfStreaming',
		eventType: EVENT_TYPE.OPERATIONAL,
		attributes: {
			traceId,
			outcomeClass,
			attribution,
			confidence,
			detectionLayer,
			symptom,
			finalJsonInvalid,
			finalJsonRepaired,
			finalAdfInvalid,
			finalAdfRepaired,
		},
	});
}

export function fireCommandError({
	api,
	command,
	traceId,
	isCreateAction,
	error,
}: {
	api: API;
	command: QueuedStreamCommand | null;
	error: unknown;
	isCreateAction: boolean | undefined;
	traceId: string | undefined;
}): void {
	api?.analytics?.actions.fireAnalyticsEvent<EditorAIAnalyticEventPayload, 'customEventType'>({
		action: 'error',
		actionSubject: 'editorPluginAI',
		actionSubjectId: 'adfStreaming',
		eventType: EVENT_TYPE.OPERATIONAL,
		attributes: {
			errorMessage: 'toolCallError',
			errorData: {
				commandType: command?.type,
				commandReason: command?.type === 'endStream' ? command?.reason : undefined,
				errorMessage: ErrorUtils.extractErrorMessage(error),
			},
			traceId,
			experienceName: null,
			selectionType: null,
			selectionCategory: null,
			streamId: null,
			source: { type: 'orchestrator', isCreateAction: isCreateAction ?? false },
		},
	});
}

/**
 * Per-tool-call anomaly symptom union. Each value is a distinct apply-step
 * fault detected by the orchestrator:
 *
 * - `apply_failed_*` — an exception was thrown by the apply layer.
 * - `target_resolution_failed` — command targeted a `localId` that does
 *   not exist in the document.
 * - `unexpected_node_removal` — a top-level node disappeared after a
 *   command that wasn't supposed to remove it.
 * - `no_document_change` — a mutating command produced no document change.
 *
 * All values are mutually exclusive within a single command's apply pass.
 */
export type ToolCallAnomalySymptom =
	| ApplyErrorSymptom
	| 'target_resolution_failed'
	| 'unexpected_node_removal'
	| 'no_document_change';

/**
 * Payload type for the unified `tool_call_anomaly` event — fired when the
 * orchestrator detects any per-tool-call apply-step fault. The `symptom`
 * field discriminates between the four mutually-exclusive cases; the
 * symptom-specific attributes are correspondingly optional.
 *
 * All `localId`-bearing fields are scrubbed via `scrubStr` before being
 * placed on the wire so any email/URL substrings the model may have echoed
 * into the IDs are redacted (the same precedent as `find-node-by-local-id`).
 *
 * `attribution` and `confidence` are derived via the shared `attributeError`
 * routing table for symptoms that have an entry; for `no_document_change`
 * they are omitted (the design doc says this signal alone is insufficient
 * to attribute fault).
 */
type ToolCallAnomalyPayload = {
	action: 'toolCallAnomaly';
	actionSubject: 'editorPluginAI';
	actionSubjectId: 'adfStreaming';
	attributes: {
		attribution?: Attribution;
		commandType: string;
		confidence?: Confidence;
		detectionLayer: DetectionLayer;
		errorMessage?: string;
		scrubbedRemovedLocalIds?: string[];
		scrubbedTargetLocalId?: string;
		symptom: ToolCallAnomalySymptom;
		traceId: string | undefined;
	};
	eventType: EVENT_TYPE.OPERATIONAL;
};

/**
 * Fires the unified `tool_call_anomaly` event for any per-tool-call fault
 * detected by the orchestrator's apply pipeline. Routes the supplied
 * `symptom` through the shared `attributeError` table to derive
 * `(attribution, confidence)` so all anomaly events use a consistent
 * ownership signal.
 *
 * The detection layer is always `'orchestrator'` for this event — the
 * orchestrator's apply pipeline is what observes the anomaly, regardless
 * of whether the underlying cause is upstream (model output) or local
 * (apply-logic bug).
 *
 * Symptom-specific attributes are passed through the optional fields:
 * - `errorMessage` for `apply_failed_*` symptoms.
 * - `targetLocalId` (scrubbed before placement) for symptoms whose
 *   originating command had a target.
 * - `removedLocalIds` (scrubbed before placement) for
 *   `unexpected_node_removal`.
 *
 * `attribution` and `confidence` are omitted for `no_document_change` (per
 * the design doc, this signal alone is insufficient to attribute fault).
 */
export function fireToolCallAnomaly({
	api,
	traceId,
	commandType,
	symptom,
	errorMessage,
	targetLocalId,
	removedLocalIds,
}: {
	api: API;
	commandType: string;
	errorMessage?: string;
	removedLocalIds?: string[];
	symptom: ToolCallAnomalySymptom;
	/**
	 * The model-supplied target `localId` (or `undefined` for commands
	 * without one). Scrubbed via `scrubStr` before placement on the wire.
	 */
	targetLocalId?: string;
	traceId: string | undefined;
}): void {
	const routed =
		symptom === 'no_document_change' ? undefined : attributeError(symptom, 'orchestrator');

	api?.analytics?.actions.fireAnalyticsEvent<ToolCallAnomalyPayload, 'customEventType'>({
		action: 'toolCallAnomaly',
		actionSubject: 'editorPluginAI',
		actionSubjectId: 'adfStreaming',
		eventType: EVENT_TYPE.OPERATIONAL,
		attributes: {
			commandType,
			detectionLayer: 'orchestrator',
			symptom,
			traceId,
			...(routed !== undefined && {
				attribution: routed.attribution,
				confidence: routed.confidence,
			}),
			...(errorMessage !== undefined && { errorMessage }),
			...(targetLocalId !== undefined && { scrubbedTargetLocalId: scrubStr(targetLocalId) }),
			...(removedLocalIds !== undefined && {
				scrubbedRemovedLocalIds: removedLocalIds.map((id) => scrubStr(id)),
			}),
		},
	});
}
